import { fetchUserNotifications, fetchUserPagedData, fetchUserRoles, fetchUsersByIds, manageUpdateUser } from "@/lib/services/user"
import { User, UserNotification, UserRole } from "@/lib/types/user"
import { defineStore } from "pinia"

interface UserState {
    users: User[]
    roles: UserRole[]
    loading: boolean
}

export const useUsersStore = defineStore('user', {
    state: (): UserState => ({
        users: [],
        roles: [],
        loading: false,
    }),
    actions: {
        async _initialize() {
            await this.fetchRoles()
        },

        async fetchPagedData(page: number = 1, pageSize: number, sortField?: string, sortOrder?: number, filters?: Record<string, any>): Promise<PagedResultMetadata> {
            this.loading = true

            try {
                const params: PagedRequestParams = {
                    offset: (page - 1) * pageSize,
                    limit: pageSize,
                    sortField: sortField,
                    sortOrder: sortOrder && sortOrder === 1 ? 'ASC' : 'DESC',
                    filters: filters
                }

                const res = await fetchUserPagedData(this.roles, params)
                const { items, ...metadata } = res

                this.users = items
                return metadata as PagedResultMetadata
            } catch (err) {
                console.error(err)
                throw err
            } finally {
                this.loading = false
            }
        },

        async fetchByIds(ids: string[]): Promise<void> {
            this.loading = true
            try {
                const users = await fetchUsersByIds(ids, this.roles)
                this.users = users
            } catch (err) {
                console.error(err)
                throw err
            } finally {
                this.loading = false
            }
        },

        async updateOne(user: User): Promise<ErrorMessage | void> {
            this.loading = true

            try {
                const updatedUser = await manageUpdateUser(user)
                if (updatedUser && 'errorCode' in updatedUser) {
                    return updatedUser
                }

                const index = this.users.findIndex(u => u.id === updatedUser.id)
                if (index !== -1) {
                    this.users[index] = updatedUser
                }
            } catch (err) {
                console.error(err)
                throw err
            } finally {
                this.loading = false
            }
        },

        async fetchRoles(): Promise<void> {
            this.loading = true
            try {
                const res = await fetchUserRoles()
                this.roles = res
            } catch (err) {
                console.error(err)
                throw err
            } finally {
                this.loading = false
            }
        },

        async getNotificationsByUserId(userid: string): Promise<Record<string, UserNotification>> {
            this.loading = true

            try {
                const res = await fetchUserNotifications(userid)
                return res
            } catch (err) {
                console.error(err)
                throw err
            } finally {
                this.loading = false
            }
        },
    }
})